import {
    BorderAlignmentTypes,
    ColorParams,
    defaultColor,
    ORDINARY_STROKE_COLOR_OBJ,
    StrokeType,
    TextBackground,
    TRANSPARENT_COLOR,
} from '@bynder-studio/misc';
import { IStructuredGlyph, ShapedLine, ShapedText } from '@bynder-studio/structured-text';
import { Runs } from '@bynder-studio/structured-text/src/Core/StructuredText';
import { Box } from '../Shared/Box';
import { Dimension } from '../Shared/Dimension';
import { Position } from '../Shared/Position';
import { Region } from '../Shared/Region';
import { Mask } from '../Shared/Mask';
import type { BlendMode } from '../Shared/BlendMode';
import type { DropShadow } from '../Shared/DropShadow';
import { BaseCompElement } from './Elements/BaseCompElement';
import { GroupCompElement } from './Elements/GroupCompElement';
import { ShapeCompElement, type ShapeCompElementParams } from './Elements/ShapeCompElement';
import { SvgCompElement, SvgCompElementPool } from './Elements/SvgCompElement';
import { layOutGroup, TextCompParams, TextGroupParams, TextShapeParams, TextSvgParams } from './TextCompParams';
import { MaskModeTypes } from '../../Enums/MaskModeTypes';
import { createRectanglePath, generateCurvedPath } from '../../Helpers/pathFunctions';
import { equals } from '../../Helpers/utils';
import {
    getNextVisibleLineIndex,
    getPrevVisibleLineIndex,
    getShapedLinesBoxesWithBg,
} from '../Elements/Helpers/TextHelpers';
import type { IAssetsLoader } from '../../AssetLoader/IAssetsLoader';

export type TextCompElementContext = {
    id: BaseCompElement['id'];
    renderOrder: number;
    position: Position;
    dimension: Dimension;
    opacity: number;
    dropShadow: DropShadow | null;
    blendMode: BlendMode | null;
    isAssetLoading: boolean;
    textBackground: TextBackground | null;
    showOversetBox: boolean;
    limitTextToBounds: boolean;
    svgCompElementPool: SvgCompElementPool;
    frameIndex: number;
    sharing: {
        owner: object;
        layoutVersion: number;
        frameKey: string;
    } | null;
};

export type TextParams = {
    root: TextGroupParams;
    lines: TextGroupParams[];
    words: TextGroupParams[];
    chars: TextSvgParams[];
    hasLines: boolean;
    isNew: boolean;
    creation: {
        context: TextCompElementContext;
        cacheable: boolean;
        treeKey: string | null;
        rootOffsetX: number;
        rootOffsetY: number;
        rootDimension: Dimension;
    };
};

type SharedTree = {
    rootChildren: BaseCompElement[];
    rootOffsetX: number;
    rootOffsetY: number;
    rootDimension: Dimension;
    hasLines: boolean;
};

type CreationContext = {
    context: TextCompElementContext;
    cacheable: boolean;
    instances: Map<TextCompParams, BaseCompElement>;
    uncached: Set<BaseCompElement>;
};

type GlyphPlan = {
    glyph: IStructuredGlyph;
    id: string;
    color: ColorParams;
    strokeOverflow: number;
    left: number;
    top: number;
    width: number;
    height: number;
    absX: number;
    absY: number;
    strokeGradientRegion: Region | null;
    colorGradientRegion: Region | null;
    params: TextSvgParams | null;
};

type WordPlan = { id: string; glyphs: GlyphPlan[] };

type LinePlan = { id: string; words: WordPlan[] };

type GlyphAppearance = {
    contentBox: Box;
    pathPosition: Position;
    boundingBoxDimension: Dimension;
};

type ColorRegionsMap = Map<
    GlyphPlan,
    {
        region: Region;
        color: ColorParams;
        subregions: Region[];
        lineIds: Set<GroupCompElement['id']>;
    }
>;

type PreviousObjectForColorMaskData = {
    prevCharColor: ColorParams | null;
    prevGlyphPlan: GlyphPlan | null;
};

type SpanCollector = {
    collect: (plan: GlyphPlan) => void;
    assign: (setRegion: (plan: GlyphPlan, region: Region) => void) => void;
};

type MetaData = {
    requireTempLayer: boolean;
    hasTextBackground: boolean;
    hasGradients: boolean;
    hasStrokeGradients: boolean;
    hasOutlineColors: boolean;
    paintFillGradientsDirectly: boolean;
    colorRegions: ColorRegionsMap;
    previousObjectForColorMaskData: PreviousObjectForColorMaskData;
    strokeSpans: SpanCollector;
    fillSpans: SpanCollector;
};

type BuildState = {
    context: TextCompElementContext;
    shapedText: ShapedText;
    metadata: MetaData;
    glyphPlans: GlyphPlan[];
    linePlans: LinePlan[];
    wordsCount: number;
    charsCount: number;
    linesCount: number;
    words: TextGroupParams[];
};

export class SharedChildrenGroupCompElement extends GroupCompElement {
    getChildren(): BaseCompElement[] {
        const children = super.getChildren();

        for (const child of children) {
            child.parent = this;
        }

        return children;
    }
}

const APPEARANCE_CACHE_GENERATION_SIZE = 10000;
const TREE_CACHE_GENERATION_SIZE = 500;
const ELEMENT_CACHE_GENERATION_SIZE = 20000;

const quantize = (value: number) => Math.round(value * 1e6);

const copyBox = (box: Box) => new Box(box.position.getCopy(), box.dimension.getCopy());

const isSpace = (glyph: IStructuredGlyph) => glyph.data.text.length === 1 && glyph.data.text[0] === ' ';
const isBreak = (glyph: IStructuredGlyph) => glyph.data.text.length === 1 && glyph.data.text[0] === '\n';
const isPrevSpace = (glyphs: IStructuredGlyph[], index: number) => index > 0 && isSpace(glyphs[index - 1]);
const isDecorated = (glyph: IStructuredGlyph) => !!glyph.data?.decorationPaths?.length;

class TwoGenerationCache<V> {
    private current = new Map<string, V>();

    private previous = new Map<string, V>();

    constructor(private readonly generationSize: number) {}

    get(key: string): V | undefined {
        const value = this.current.get(key);

        if (value !== undefined) {
            return value;
        }

        const previousValue = this.previous.get(key);

        if (previousValue !== undefined) {
            this.set(key, previousValue);
        }

        return previousValue;
    }

    set(key: string, value: V) {
        if (this.current.size >= this.generationSize) {
            this.previous = this.current;
            this.current = new Map();
        }

        this.current.set(key, value);
    }

    clear() {
        this.current.clear();
        this.previous.clear();
    }
}

const getColorMaskData = (
    colorRegions: ColorRegionsMap,
    color: ColorParams,
    region: Region,
    plan: GlyphPlan,
    prev: PreviousObjectForColorMaskData,
) => {
    if (equals(prev.prevCharColor, color) && colorRegions.has(prev.prevGlyphPlan)) {
        const data = colorRegions.get(prev.prevGlyphPlan);

        colorRegions.set(plan, data);
        prev.prevGlyphPlan = plan;

        return data;
    }

    prev.prevCharColor = color;
    prev.prevGlyphPlan = plan;

    const data = {
        region,
        color,
        subregions: [],
        lineIds: new Set<GroupCompElement['id']>(),
    };

    colorRegions.set(plan, data);

    return data;
};

const getPaintedRegion = (shapedText: ShapedText, plan: GlyphPlan) => {
    const stroke = shapedText.getTextPropsProperty('stroke', plan.glyph.data.runIdx);
    const region = new Region(plan.absX, plan.absY, plan.width + plan.absX, plan.height + plan.absY);

    if (stroke.width > 0 && (stroke.type === StrokeType.OUTSIDE || stroke.type === StrokeType.CENTER)) {
        const offset = stroke.width / (stroke.type === StrokeType.CENTER ? 2 : 1);

        region.top = Math.floor(region.top - offset);
        region.left = Math.floor(region.left - offset);
        region.right = Math.ceil(region.right + offset);
        region.bottom = Math.ceil(region.bottom + offset);
    }

    return region;
};

const createColorShapes = (
    shapedText: ShapedText,
    colorRegions: ColorRegionsMap,
    plan: GlyphPlan,
    prev: PreviousObjectForColorMaskData,
) => {
    if ((isSpace(plan.glyph) && !isDecorated(plan.glyph)) || isBreak(plan.glyph)) {
        return;
    }

    const color = shapedText.getTextPropsProperty('color', plan.glyph.data.runIdx);
    const region = getPaintedRegion(shapedText, plan);
    const maskData = getColorMaskData(colorRegions, color, region, plan, prev);

    const updatedRegion = maskData.region.getUnion(region);

    maskData.region = updatedRegion;
    maskData.subregions.push(region);
};

const createSpanCollector = (
    shapedText: ShapedText,
    getRunColor: (runIdx: number) => ColorParams | null | undefined,
): SpanCollector => {
    let prevColor: ColorParams | null = null;
    let prevSpan: { region: Region } | null = null;
    const spans = new Map<GlyphPlan, { span: { region: Region }; origin: Position }>();

    return {
        collect: (plan: GlyphPlan) => {
            if ((isSpace(plan.glyph) && !isDecorated(plan.glyph)) || isBreak(plan.glyph)) {
                return;
            }

            const color = getRunColor(plan.glyph.data.runIdx);

            if (!color || color.type === 'SOLID') {
                return;
            }

            const region = getPaintedRegion(shapedText, plan);

            if (equals(prevColor, color) && prevSpan) {
                prevSpan.region = prevSpan.region.getUnion(region);
            } else {
                prevColor = color;
                prevSpan = { region };
            }

            spans.set(plan, {
                span: prevSpan,
                origin: new Position(plan.absX - plan.strokeOverflow, plan.absY - plan.strokeOverflow),
            });
        },
        assign: (setRegion: (plan: GlyphPlan, region: Region) => void) => {
            spans.forEach(({ span, origin }, plan) => {
                setRegion(plan, span.region.getTranslation(-origin.getX(), -origin.getY()));
            });
        },
    };
};

const shouldStartWordWith = (glyph: IStructuredGlyph, isSpaceForBreak: boolean, metadata: MetaData) =>
    !isSpaceForBreak &&
    (glyph.data.path !== '' || glyph.data.decorationPaths || (metadata.hasTextBackground && !isBreak(glyph)));

/**
 * Text breakup can cause parts to fall outside of the canvas
 * When motion blur is enabled, this can cause the side-effect that an
 *   invalid translation is applied to parts where the initial position (pre-transform) is the end location.
 * To prevent these side-effects, we remove the invisible elements (marked by the breakup animation functions)
 * NOTE: that these elements are invisible anyway.
 */
export const removeHiddenParts = (group: TextGroupParams) => {
    group.children = group.children.filter((child) => !(child instanceof TextCompParams && child.remove));
    group.children.forEach((child) => {
        if (child instanceof TextGroupParams) {
            removeHiddenParts(child);
        }
    });
};

export class TextCompElementFactory {
    private static instance: TextCompElementFactory | null = null;

    private assetLoader!: IAssetsLoader;

    private appearances = new TwoGenerationCache<GlyphAppearance>(APPEARANCE_CACHE_GENERATION_SIZE);

    private trees = new TwoGenerationCache<SharedTree>(TREE_CACHE_GENERATION_SIZE);

    private elements = new TwoGenerationCache<BaseCompElement>(ELEMENT_CACHE_GENERATION_SIZE);

    private contentIds = new TwoGenerationCache<number>(TREE_CACHE_GENERATION_SIZE);

    private elementContents = new WeakMap<object, { stamp: string; contentId: number; cacheable: boolean }>();

    private objectIds = new WeakMap<object, number>();

    private valueIds = new Map<string, number>();

    private jsonIds = new WeakMap<object, number>();

    private nextId = 1;

    static getInstance(): TextCompElementFactory {
        TextCompElementFactory.instance ??= new TextCompElementFactory();

        return TextCompElementFactory.instance;
    }

    setAssetLoader(assetLoader: IAssetsLoader) {
        this.assetLoader = assetLoader;
    }

    getTextParams(shapedText: ShapedText, runs: Runs, context: TextCompElementContext): TextParams {
        const { sharing } = context;
        const content = sharing ? this.getContent(shapedText, runs, context) : null;
        const cacheable = !!content?.cacheable;
        const treeKey = cacheable ? `${content.contentId}|${sharing.frameKey}` : null;
        const tree = treeKey ? this.trees.get(treeKey) : undefined;

        if (tree) {
            return this.getReusedTextParams(tree, context);
        }

        const text = this.buildTextParams(shapedText, runs, context);

        text.creation.cacheable = cacheable;
        text.creation.treeKey = treeKey;

        return text;
    }

    private getReusedTextParams(tree: SharedTree, context: TextCompElementContext): TextParams {
        const root = new TextGroupParams();

        root.renderOrder = context.renderOrder;
        root.children = [...tree.rootChildren];
        root.contentBox = new Box(new Position(0, 0), tree.rootDimension.getCopy());
        root.boundingBox = new Box(
            new Position(context.position.getX() + tree.rootOffsetX, context.position.getY() + tree.rootOffsetY),
            tree.rootDimension.getCopy(),
        );

        return {
            root,
            lines: [],
            words: [],
            chars: [],
            hasLines: tree.hasLines,
            isNew: false,
            creation: {
                context,
                cacheable: true,
                treeKey: null,
                rootOffsetX: tree.rootOffsetX,
                rootOffsetY: tree.rootOffsetY,
                rootDimension: tree.rootDimension,
            },
        };
    }

    private buildTextParams(shapedText: ShapedText, runs: Runs, context: TextCompElementContext): TextParams {
        const { textBackground } = context;

        const strokeSpans = createSpanCollector(shapedText, (runIdx) => {
            const stroke = shapedText.getTextPropsProperty('stroke', runIdx);

            return stroke?.width && stroke.type !== StrokeType.NONE ? stroke.strokeColor : null;
        });
        const fillSpans = createSpanCollector(shapedText, (runIdx) => shapedText.getTextPropsProperty('color', runIdx));

        const hasGradients = runs.some((run) => run.color.type !== 'SOLID');
        const hasStrokeGradients = runs.some(
            (run) => !!run.stroke?.strokeColor && run.stroke.strokeColor.type !== 'SOLID',
        );
        const hasOutlineColors = runs.some(
            (run) => !!run.stroke?.strokeColor && !!run.stroke.width && run.stroke.type !== StrokeType.NONE,
        );

        const metadata: MetaData = {
            requireTempLayer: false,
            hasTextBackground: !!textBackground,
            hasGradients,
            hasStrokeGradients,
            hasOutlineColors,
            paintFillGradientsDirectly: hasGradients && hasOutlineColors,
            colorRegions: new Map(),
            previousObjectForColorMaskData: {
                prevCharColor: null,
                prevGlyphPlan: null,
            },
            strokeSpans,
            fillSpans,
        };

        const shapedLines = shapedText.getShapedLines();
        const state: BuildState = {
            context,
            shapedText,
            metadata,
            glyphPlans: [],
            linePlans: [],
            wordsCount: 0,
            charsCount: 0,
            linesCount: shapedLines.length,
            words: [],
        };

        shapedLines.forEach((line, lineIdx) => {
            this.planLine(state, line, lineIdx);
        });

        strokeSpans.assign((plan, region) => {
            plan.strokeGradientRegion = region;
        });
        fillSpans.assign((plan, region) => {
            plan.colorGradientRegion = region;
        });

        const lines = state.linePlans.map((linePlan) => this.getLineParams(state, linePlan));
        const chars = state.glyphPlans.map((plan) => plan.params ?? this.getCharParams(state, plan));

        const rootChildren: TextCompParams[] = [];

        if (lines.length) {
            const backgrounds = this.getBackgroundsParams(shapedText, context);
            const hasBgGradient = textBackground?.isActive() && textBackground.color.type !== 'SOLID';
            const borders = backgrounds && hasBgGradient ? this.getBordersParams(backgrounds, context) : null;
            const bgGradients =
                backgrounds && hasBgGradient ? this.getBackgroundGradientParams(backgrounds, context) : null;
            const gradients = this.getTextGradientsParams(state, lines);
            const text = this.getTextGroupParams(state, lines, gradients);

            rootChildren.push(...[bgGradients, backgrounds, borders, text, gradients].filter(Boolean));
        }

        if (context.showOversetBox && context.limitTextToBounds) {
            rootChildren.push(this.getOversetBoxParams(context));
        }

        const root = new TextGroupParams();
        root.renderOrder = context.renderOrder;
        layOutGroup(root, rootChildren);

        return {
            root,
            lines,
            words: state.words,
            chars,
            hasLines: lines.length > 0,
            isNew: true,
            creation: {
                context,
                cacheable: false,
                treeKey: null,
                rootOffsetX: root.boundingBox.position.getX() - context.position.getX(),
                rootOffsetY: root.boundingBox.position.getY() - context.position.getY(),
                rootDimension: root.boundingBox.dimension.getCopy(),
            },
        };
    }

    private planLine(state: BuildState, line: ShapedLine, lineIdx: number) {
        const { context, metadata, shapedText, linesCount } = state;
        const lineId = context.id + '_' + state.linePlans.length; // unique ID
        const words: WordPlan[] = [];
        let compWord: GlyphPlan[] = [];

        const flushWord = () => {
            if (compWord.length === 0) {
                return;
            }

            words.push({ id: lineId + '_' + state.wordsCount, glyphs: compWord }); // unique ID
            state.wordsCount++;
        };

        line.glyphs.forEach((glyph, index) => {
            const plan = this.planGlyph(state, glyph, lineId + '_' + state.wordsCount + '_' + state.charsCount);

            state.charsCount++;
            state.glyphPlans.push(plan);

            if (metadata.hasGradients && !metadata.paintFillGradientsDirectly) {
                createColorShapes(shapedText, metadata.colorRegions, plan, metadata.previousObjectForColorMaskData);
            }

            if (metadata.paintFillGradientsDirectly) {
                metadata.fillSpans.collect(plan);
            }

            if (metadata.hasStrokeGradients) {
                metadata.strokeSpans.collect(plan);
            }

            if (!metadata.requireTempLayer && (glyph.data?.strokeType === StrokeType.OUTSIDE || glyph.data?.punchOut)) {
                metadata.requireTempLayer = true;
            }

            const currentIsSpace = isSpace(glyph);
            const prevIsSpace = isPrevSpace(line.glyphs, index);
            const isSpaceForBreak = currentIsSpace && index === line.glyphs.length - 1 && lineIdx !== linesCount - 1;

            if (isBreak(glyph) || (currentIsSpace && !prevIsSpace) || (prevIsSpace && !currentIsSpace)) {
                flushWord();
                compWord = [];

                if (shouldStartWordWith(glyph, isSpaceForBreak, metadata)) {
                    compWord.push(plan);
                }
            } else {
                compWord.push(plan);
            }
        });

        flushWord();

        if (words.length === 0) {
            return;
        }

        if (metadata.hasGradients && !metadata.paintFillGradientsDirectly) {
            // add the line id to containing glyphs
            // will be used to create a mask
            words.forEach((word) => {
                word.glyphs.forEach((plan) => {
                    metadata.colorRegions.get(plan)?.lineIds.add(lineId);
                });
            });
        }

        state.linePlans.push({ id: lineId, words });
    }

    private planGlyph(state: BuildState, structuredGlyph: IStructuredGlyph, id: string): GlyphPlan {
        const { context, metadata, shapedText } = state;

        // gradient color is applied as mask, so we remove it here and create mask element later —
        // unless this element paints its fill gradient onto the glyph, where the mask isn't used
        const color = shapedText.getTextPropsProperty('color', structuredGlyph.data.runIdx);

        // Content bbox relative to text element bbox
        // We set 1px with as a nominal value for empty letter in case of textBg -
        // otherwise filter fn just count this element to be not-visible
        const absContentBbox =
            structuredGlyph.isNewLine() && context.textBackground?.isActive()
                ? { ...structuredGlyph.data.absBbox, width: 1 }
                : structuredGlyph.data.absContentBbox;

        return {
            glyph: structuredGlyph,
            id,
            color: color.type === 'SOLID' || metadata.paintFillGradientsDirectly ? color : defaultColor,
            strokeOverflow:
                structuredGlyph.data.strokeType === StrokeType.OUTSIDE ? structuredGlyph.data.strokeWidth : 0,
            left: absContentBbox.left,
            top: absContentBbox.top,
            width: absContentBbox.width,
            height: absContentBbox.height,
            absX: context.position.getX() + absContentBbox.left,
            absY: context.position.getY() + absContentBbox.top,
            strokeGradientRegion: null,
            colorGradientRegion: null,
            params: null,
        };
    }

    private getLineParams(state: BuildState, linePlan: LinePlan): TextGroupParams {
        const { context, metadata } = state;
        const words = linePlan.words.map((wordPlan) => {
            const word = this.getWordParams(state, wordPlan);

            state.words.push(word);

            return word;
        });
        const line = new TextGroupParams();

        layOutGroup(line, words);
        line.id = linePlan.id;
        line.name = 'line_' + line.id;
        line.renderOrder = context.renderOrder;

        if (metadata.hasGradients && !metadata.paintFillGradientsDirectly) {
            // the line is used as a mask for the text gradient
            line.usedAsInternalMask = true;
        }

        return line;
    }

    private getWordParams(state: BuildState, wordPlan: WordPlan): TextGroupParams {
        const chars = wordPlan.glyphs.map((plan) => {
            plan.params = this.getCharParams(state, plan);

            return plan.params;
        });
        const word = new TextGroupParams();

        layOutGroup(word, chars);
        word.id = wordPlan.id;
        word.name = 'word_' + word.id;
        word.renderOrder = state.context.renderOrder;

        return word;
    }

    private getCharParams(state: BuildState, plan: GlyphPlan): TextSvgParams {
        const { context } = state;
        const { data } = plan.glyph;
        const appearance = this.getAppearance(plan);
        const char = new TextSvgParams();

        char.isGlyph = true;
        char.id = plan.id;
        char.color = plan.color;
        char.path = this.assetLoader.makePath(data);
        char.decorationPaths = data.decorationPaths;
        char.horizontalPathScale = data.pathScale;
        char.verticalPathScale = -1 * data.pathScale; // char glyphs are defined upside down
        char.isAssetLoading = context.isAssetLoading;
        char.renderOrder = context.renderOrder;
        char.strokeWidth = data.strokeWidth;
        char.strokeType = data.strokeType;
        char.strokeColor = data.strokeColor || null;
        char.strokeGradientRegion = plan.strokeGradientRegion;
        char.colorGradientRegion = plan.colorGradientRegion;
        char.punchOut = data.punchOut;
        char.glyph = data;

        char.contentBox = appearance.contentBox;
        char.pathPosition = appearance.pathPosition;
        char.boundingBox = new Box(new Position(plan.absX, plan.absY), appearance.boundingBoxDimension);

        return char;
    }

    private getAppearance(plan: GlyphPlan): GlyphAppearance {
        const { data } = plan.glyph;
        const { strokeOverflow, width, height } = plan;
        const pathX = data.absLeftPx - plan.left + strokeOverflow;
        const pathY = data.absTopPx - plan.top + strokeOverflow;
        const key = `${strokeOverflow}|${width}|${height}|${pathX}|${pathY}`;
        const cached = this.appearances.get(key);

        if (cached) {
            return cached;
        }

        const appearance: GlyphAppearance = {
            contentBox: new Box(
                new Position(-strokeOverflow, -strokeOverflow),
                new Dimension(width + strokeOverflow * 2, height + strokeOverflow * 2),
            ),
            pathPosition: new Position(pathX, pathY),
            boundingBoxDimension: new Dimension(width, height),
        };

        this.appearances.set(key, appearance);

        return appearance;
    }

    private getTextGroupParams(
        state: BuildState,
        lines: TextGroupParams[],
        gradients: TextGroupParams | null,
    ): TextGroupParams {
        const { context, metadata } = state;
        const text = new TextGroupParams();

        layOutGroup(text, lines);
        text.id = context.id + '_text_' + lines.length;
        text.name = `text_${text.id}`;
        text.renderOrder = context.renderOrder;
        text.requireTempLayer = metadata.requireTempLayer;
        text.opacity = context.opacity;
        const shadowTarget = gradients ?? text;
        shadowTarget.dropShadow = context.dropShadow;
        text.blendMode = context.blendMode;

        return text;
    }

    private getTextGradientsParams(state: BuildState, lines: TextGroupParams[]): TextGroupParams | null {
        const { context, metadata } = state;
        const linesById = new Map(lines.map((line) => [line.id, line]));
        const textGradients = [...new Set(metadata.colorRegions.values())]
            .flatMap((data) => {
                // separate the data for each line
                return [...data.lineIds].map((lineId) => ({
                    region: data.region,
                    color: data.color,
                    subregions: data.subregions,
                    lineId,
                }));
            })
            .map(({ region, color, subregions, lineId }, i) =>
                this.getTextGradientParams(context, { region, color, subregions, line: linesById.get(lineId) }, i),
            );

        if (!textGradients.length) {
            return null;
        }

        const gradients = new TextGroupParams();

        layOutGroup(gradients, textGradients);
        gradients.id = `${context.id}_gradients`;
        gradients.name = `gradients_${gradients.id}`;
        gradients.renderOrder = context.renderOrder;

        return gradients;
    }

    private getTextGradientParams(
        context: TextCompElementContext,
        gradient: { region: Region; color: ColorParams; subregions: Region[]; line: TextGroupParams },
        index: number,
    ): TextSvgParams {
        const { region, subregions } = gradient;
        const textGradient = new TextSvgParams();

        textGradient.id = `${context.id}_text_gradient__${index}`;
        textGradient.color = gradient.color;
        textGradient.isAssetLoading = context.isAssetLoading;
        textGradient.renderOrder = context.renderOrder;
        textGradient.pathPosition = new Position(0, 0);

        const dimensions = new Dimension(region.getWidth(), region.getHeight());

        textGradient.path = subregions.reduce((path, subregion) => {
            const rect = createRectanglePath(
                subregion.left - region.left,
                subregion.top - region.top,
                subregion.getWidth(),
                subregion.getHeight(),
            );

            return `${path} ${rect}`;
        }, '');
        textGradient.contentBox = new Box(new Position(0, 0), dimensions.getCopy());
        textGradient.boundingBox = new Box(new Position(region.left, region.top), dimensions);
        textGradient.maskTarget = gradient.line;

        return textGradient;
    }

    private getBackgroundsParams(shapedText: ShapedText, context: TextCompElementContext): TextGroupParams | null {
        const backgrounds = this.getBackgroundShapesParams(shapedText, context);

        if (!backgrounds.length) {
            return null;
        }

        const { textBackground } = context;
        const group = new TextGroupParams();

        layOutGroup(group, backgrounds);
        group.id = `${context.id}_background`;
        group.name = `background_${group.id}`;
        group.renderOrder = context.renderOrder;
        group.usedAsInternalMask = textBackground?.isActive() && textBackground.color.type !== 'SOLID';

        return group;
    }

    private getBordersParams(backgrounds: TextGroupParams, context: TextCompElementContext): TextGroupParams {
        const borders = (backgrounds.children as TextShapeParams[]).map((background) => {
            const border = new TextShapeParams();

            border.borderOf = background;
            border.contentBox = copyBox(background.contentBox);
            border.boundingBox = copyBox(background.boundingBox);

            return border;
        });
        const group = new TextGroupParams();

        group.children = borders;
        group.contentBox = copyBox(backgrounds.contentBox);
        group.boundingBox = copyBox(backgrounds.boundingBox);
        borders.forEach((border) => {
            border.parent = group;
        });
        group.id = `${context.id}_background_borders`;
        group.name = `background_borders_${backgrounds.id}`;

        return group;
    }

    private getBackgroundGradientParams(
        backgrounds: TextGroupParams,
        context: TextCompElementContext,
    ): TextGroupParams {
        const bgGradient = new TextSvgParams();

        bgGradient.id = `${context.id}_bg_gradient`;
        bgGradient.color = context.textBackground.color;
        bgGradient.isAssetLoading = context.isAssetLoading;
        bgGradient.renderOrder = context.renderOrder;
        bgGradient.pathPosition = new Position(0, 0);

        const dimensions = backgrounds.contentBox.dimension.getCopy();

        bgGradient.path = createRectanglePath(0, 0, dimensions.getWidth(), dimensions.getHeight());
        bgGradient.contentBox = new Box(new Position(0, 0), dimensions);
        bgGradient.boundingBox = new Box(backgrounds.boundingBox.position.getCopy(), dimensions);
        bgGradient.maskTarget = backgrounds;

        const group = new TextGroupParams();

        layOutGroup(group, [bgGradient]);
        group.id = `${context.id}_gradients`;
        group.name = `gradients_${group.id}`;
        group.renderOrder = context.renderOrder;

        return group;
    }

    private getOversetBoxParams(context: TextCompElementContext): TextGroupParams {
        const box = new TextSvgParams();

        box.id = `${context.id}_overset_box`; // unique ID
        box.color = ORDINARY_STROKE_COLOR_OBJ;
        box.path = createRectanglePath(0, 0, context.dimension.width, context.dimension.height);
        box.horizontalPathScale = 1;
        box.verticalPathScale = 1;
        box.isAssetLoading = context.isAssetLoading;
        box.renderOrder = context.renderOrder;
        box.strokeWidth = 4;
        box.strokeType = StrokeType.CENTER;
        box.strokeColor = null;
        box.punchOut = false;
        box.contentBox = new Box(new Position(0, 0), context.dimension.getCopy());
        box.boundingBox = new Box(context.position.getCopy(), context.dimension.getCopy());
        box.pathPosition = new Position(0, 0);

        const group = new TextGroupParams();

        layOutGroup(group, [box]);
        group.id = `${context.id}_overset_comp_box0`; // unique ID
        group.name = `overset_box_${group.id}`;
        group.renderOrder = context.renderOrder;

        return group;
    }

    private findBgIntersections(lines: ReturnType<typeof getShapedLinesBoxesWithBg>) {
        const bgMergeMap = new Map<number, number>();

        lines.forEach((cur, index) => {
            if (!index || !cur.absBbox.width || !cur.absBbox.height) {
                return;
            }

            const prevIndex = getPrevVisibleLineIndex(lines, index - 1);
            const prev = lines?.[prevIndex];

            if (prev && cur.absBbox.top <= prev.absBbox.top + prev.absBbox.height) {
                const mergeLineIdx = bgMergeMap.get(prevIndex) ?? prevIndex;

                if (mergeLineIdx >= 0 && lines[mergeLineIdx]) {
                    bgMergeMap.set(index, mergeLineIdx);
                }
            }
        });

        return bgMergeMap;
    }

    private getBackgroundShapesParams(shapedText: ShapedText, context: TextCompElementContext): TextShapeParams[] {
        const shapedLines = shapedText.getShapedLines();
        const lines = getShapedLinesBoxesWithBg(shapedLines, context.textBackground);

        if (!context.textBackground?.isActive() || !lines.length) {
            return [];
        }

        const bgMergeMap = this.findBgIntersections(lines);
        const backgrounds: TextShapeParams[] = [];

        lines.forEach((line, lineIdx) => {
            if (!line.absBbox.width || !line.absBbox.height) {
                return;
            }

            const mergeLineIdx = bgMergeMap.get(lineIdx) ?? null;
            let background: TextShapeParams | null;

            if (mergeLineIdx === null) {
                if ([...bgMergeMap.values()].includes(lineIdx)) {
                    return;
                }

                background = this.getBackgroundParams(shapedLines, context, { start: lineIdx, end: lineIdx + 1 });
            } else {
                const nextLineIndex = getNextVisibleLineIndex(lines, lineIdx + 1);
                const nextLine = lines?.[nextLineIndex];

                if (nextLine && bgMergeMap.get(nextLineIndex) !== undefined) {
                    return;
                }

                background = this.getBackgroundParams(shapedLines, context, { start: mergeLineIdx, end: lineIdx + 1 });
            }

            if (background) {
                backgrounds.push(background);
            }
        });

        return backgrounds;
    }

    private getBackgroundParams(
        shapedLines: ShapedLine[],
        context: TextCompElementContext,
        lineRange: { start: number; end: number },
    ): TextShapeParams | null {
        const { textBackground } = context;
        const lines = getShapedLinesBoxesWithBg(shapedLines.slice(lineRange.start, lineRange.end), textBackground);

        // TODO: This calculation needs to be changed and refactored
        const getPathPoints = (xOffset = 0, yOffset = 0) => {
            const cordsLeft = [];
            const cordsRight = [];

            lines.forEach((curr, index) => {
                if (curr.absBbox.width <= 0 || curr.absBbox.height <= 0) {
                    return;
                }

                const prev = lines?.[getPrevVisibleLineIndex(lines, index - 1)];
                const next = lines?.[getNextVisibleLineIndex(lines, index + 1)];
                const passSmallLine = prev?.absBbox?.top + prev?.absBbox?.height > next?.absBbox?.top;

                const hasBiggerLineAfter = () =>
                    !!lines.slice(index + 1).some((current) => current?.absBbox?.width > curr.absBbox.width);

                if (index && passSmallLine && hasBiggerLineAfter()) {
                    return;
                }

                let { left, top } = curr.absBbox;
                const { width, height } = curr.absBbox;

                left = left - xOffset;
                top = top - yOffset;

                const topToUse = cordsLeft?.[cordsLeft.length - 1]?.y ?? top;
                const right = left + width;
                const bottom = top + height;

                const fnToApply = next?.absBbox?.width > width ? Math.min : Math.max;
                const bottomToUse = next ? fnToApply(bottom, next.absBbox.top - yOffset) : bottom;

                const radius = (lines[0].absBbox.height / 2) * (textBackground.cornerRadius * 0.01);

                cordsLeft.push({ x: left, y: topToUse, r: radius });
                cordsLeft.push({ x: left, y: bottomToUse, r: radius });

                cordsRight.unshift({ x: right, y: topToUse, r: radius });
                cordsRight.unshift({ x: right, y: bottomToUse, r: radius });
            });

            return [...cordsLeft, ...cordsRight];
        };

        const xOffset = Math.min(...lines.map((l) => l.absBbox.left));
        const yOffset = Math.min(...lines.map((l) => l.absBbox.top));

        const points = getPathPoints(xOffset, yOffset);

        if (!points.length) {
            return null;
        }

        const background = new TextShapeParams();

        background.path = generateCurvedPath(points);
        background.id = `${context.id}_background_${lineRange.start}_${lineRange.end}`;
        background.color = textBackground.color;
        background.borderColor = textBackground.borderColor;
        background.borderWidth = textBackground.borderWidth;
        background.borderAlignment = textBackground.borderAlignment;
        background.borderLineCap = textBackground.borderLineCap;
        background.isAssetLoading = context.isAssetLoading;
        background.renderOrder = context.renderOrder;
        background.pathPosition = new Position(0, 0);

        const maxWidth = Math.max(...lines.map((l) => l.absBbox.width));
        const maxHeight =
            lines[lines.length - 1].absBbox.top + lines[lines.length - 1].absBbox.height - lines[0].absBbox.top;
        const dimensions = new Dimension(maxWidth, maxHeight);

        background.contentBox = new Box(new Position(xOffset, yOffset), dimensions);
        background.boundingBox = new Box(context.position.getCopy(), dimensions);

        const borderWidth = background.borderWidth || 0;

        if (borderWidth && background.borderAlignment === BorderAlignmentTypes.MIDDLE) {
            const borderOverflow = Math.ceil(borderWidth / 2);
            background.pathPosition = new Position(borderOverflow, borderOverflow);
            background.contentBox = new Box(
                new Position(xOffset - borderOverflow, yOffset - borderOverflow),
                new Dimension(dimensions.getWidth() + borderOverflow * 2, dimensions.getHeight() + borderOverflow * 2),
            );
        } else if (borderWidth && background.borderAlignment === BorderAlignmentTypes.OUTSIDE) {
            const borderOverflow = borderWidth;
            background.pathPosition = new Position(borderOverflow, borderOverflow);
            background.contentBox = new Box(
                new Position(xOffset - borderOverflow, yOffset - borderOverflow),
                new Dimension(dimensions.getWidth() + borderOverflow * 2, dimensions.getHeight() + borderOverflow * 2),
            );
        }

        return background;
    }

    createTextCompElement(
        text: TextParams,
        element: { originalElement: BaseCompElement['originalElement']; ghostContentBox: Box },
    ): GroupCompElement {
        const { root, creation } = text;
        const ctx: CreationContext = {
            context: creation.context,
            cacheable: creation.cacheable,
            instances: new Map(),
            uncached: new Set(),
        };
        const children = root.children.map((child) => this.create(child, ctx));

        if (text.isNew && creation.treeKey && !children.some((child) => ctx.uncached.has(child))) {
            this.trees.set(creation.treeKey, {
                rootChildren: [...children],
                rootOffsetX: creation.rootOffsetX,
                rootOffsetY: creation.rootOffsetY,
                rootDimension: creation.rootDimension,
                hasLines: text.hasLines,
            });
        }

        const rootEl = new SharedChildrenGroupCompElement(children, false);

        this.assignCommon(rootEl, root);
        rootEl.originalElement = element.originalElement;
        rootEl.ghostContentBox = element.ghostContentBox;

        if (root.remove) {
            (rootEl as BaseCompElement & { remove?: boolean }).remove = true;
        }

        return rootEl;
    }

    private create(params: TextCompParams | BaseCompElement, ctx: CreationContext): BaseCompElement {
        if (params instanceof BaseCompElement) {
            return params;
        }

        const created = ctx.instances.get(params);

        if (created) {
            return created;
        }

        let compEl: BaseCompElement;

        if (params instanceof TextGroupParams) {
            compEl = this.createGroup(params, ctx);
        } else if (params instanceof TextShapeParams) {
            compEl = this.createShape(params, ctx);
        } else {
            compEl = this.createSvg(params as TextSvgParams, ctx);
        }

        ctx.instances.set(params, compEl);

        return compEl;
    }

    private createGroup(params: TextGroupParams, ctx: CreationContext): BaseCompElement {
        const children = params.children.map((child) => this.create(child, ctx));
        const cacheable = ctx.cacheable && !params.motionBlur && !children.some((child) => ctx.uncached.has(child));
        const checksum = cacheable
            ? `G|${this.getCommonChecksum(params)}|${children.map((child) => this.getObjectId(child)).join(',')}`
            : null;

        return this.reuse(checksum, ctx, () => {
            const group = new SharedChildrenGroupCompElement(children, false);

            this.assignCommon(group, params);

            return group;
        });
    }

    private createSvg(params: TextSvgParams, ctx: CreationContext): BaseCompElement {
        const maskTarget = params.maskTarget ? this.create(params.maskTarget, ctx) : null;
        const cacheable = ctx.cacheable && !params.motionBlur && !(maskTarget && ctx.uncached.has(maskTarget));
        const checksum = cacheable
            ? [
                  'S',
                  this.getCommonChecksum(params),
                  this.getValueKey(params.path),
                  this.getJsonId(params.decorationPaths),
                  params.horizontalPathScale,
                  params.verticalPathScale,
                  this.getJsonId(params.color),
                  quantize(params.pathPosition.x),
                  quantize(params.pathPosition.y),
                  params.strokeType,
                  params.strokeWidth,
                  this.getJsonId(params.strokeColor),
                  this.getRegionKey(params.strokeGradientRegion),
                  this.getRegionKey(params.colorGradientRegion),
                  params.punchOut,
                  params.glyph?.fontId,
                  this.getObjectId(maskTarget),
              ].join('|')
            : null;

        return this.reuse(checksum, ctx, () => {
            const compEl =
                !cacheable && params.isGlyph
                    ? ctx.context.svgCompElementPool.acquire(params.id as string, ctx.context.frameIndex)
                    : new SvgCompElement();

            this.assignCommon(compEl, params);
            compEl.path = params.path;
            compEl.decorationPaths = params.decorationPaths;
            compEl.horizontalPathScale = params.horizontalPathScale;
            compEl.verticalPathScale = params.verticalPathScale;
            compEl.color = params.color;
            compEl.pathPosition = params.pathPosition;
            compEl.strokeType = params.strokeType;
            compEl.strokeWidth = params.strokeWidth;
            compEl.strokeColor = params.strokeColor;
            compEl.strokeGradientRegion = params.strokeGradientRegion;
            compEl.colorGradientRegion = params.colorGradientRegion;
            compEl.punchOut = params.punchOut;
            compEl.glyph = params.glyph;

            if (maskTarget) {
                compEl.mask = new Mask({
                    elementId: maskTarget.id as number,
                    mode: MaskModeTypes.ALPHA,
                });
            }

            return compEl;
        });
    }

    private createShape(params: TextShapeParams, ctx: CreationContext): BaseCompElement {
        if (params.borderOf) {
            return this.createBorder(params, ctx);
        }

        const cacheable = ctx.cacheable && !params.motionBlur;
        const checksum = cacheable
            ? [
                  'B',
                  this.getCommonChecksum(params),
                  this.getValueKey(params.path),
                  this.getJsonId(params.color),
                  quantize(params.pathPosition.x),
                  quantize(params.pathPosition.y),
                  this.getJsonId(params.borderColor),
                  params.borderWidth,
                  params.borderAlignment,
                  params.borderLineCap,
              ].join('|')
            : null;

        return this.reuse(checksum, ctx, () => {
            const compEl = new ShapeCompElement();

            this.assignCommon(compEl, params);
            compEl.path = params.path;
            compEl.color = params.color;
            compEl.pathPosition = params.pathPosition;
            compEl.borderColor = params.borderColor;
            compEl.borderWidth = params.borderWidth;
            compEl.borderAlignment = params.borderAlignment;
            compEl.borderLineCap = params.borderLineCap;

            return compEl;
        });
    }

    private createBorder(params: TextShapeParams, ctx: CreationContext): BaseCompElement {
        const background = this.create(params.borderOf, ctx);
        const cacheable = ctx.cacheable && !ctx.uncached.has(background);
        const checksum = cacheable ? `R|${this.getObjectId(background)}` : null;

        return this.reuse(checksum, ctx, () => {
            const borderParams = background.toObject() as unknown as ShapeCompElementParams;
            borderParams.id += '_border';
            borderParams.name += '_border';
            borderParams.color = TRANSPARENT_COLOR;
            const border = new ShapeCompElement();
            border.setProperties(borderParams);

            return border;
        });
    }

    private reuse(checksum: string | null, ctx: CreationContext, create: () => BaseCompElement): BaseCompElement {
        if (checksum === null) {
            const compEl = create();

            ctx.uncached.add(compEl);

            return compEl;
        }

        const cached = this.elements.get(checksum);

        if (cached) {
            return cached;
        }

        const compEl = create();

        if (compEl.usedAsInternalMask) {
            compEl.id = `${compEl.id}~${this.nextId++}`;
        }

        this.elements.set(checksum, compEl);

        return compEl;
    }

    private assignCommon(compEl: BaseCompElement, params: TextCompParams) {
        compEl.id = params.id;
        compEl.name = params.name;
        compEl.renderOrder = params.renderOrder;
        compEl.contentBox = params.contentBox;
        compEl.boundingBox = params.boundingBox;
        compEl.hidden = params.hidden;
        compEl.opacity = params.opacity;
        compEl.rotation = params.rotation;
        compEl.scale = params.scale;
        compEl.blur = params.blur;
        compEl.clipPath = params.clipPath;
        compEl.clipSoftness = params.clipSoftness;
        compEl.dropShadow = params.dropShadow;
        compEl.mask = params.mask;
        compEl.usedAsInternalMask = params.usedAsInternalMask;
        compEl.requireTempLayer = params.requireTempLayer;
        compEl.isInAnimation = params.isInAnimation;
        compEl.blendMode = params.blendMode;
        compEl.motionBlur = params.motionBlur;
        compEl.translatedX = params.translatedX;
        compEl.translatedY = params.translatedY;
        compEl.isAssetLoading = params.isAssetLoading;
    }

    private getCommonChecksum(params: TextCompParams): string {
        const { boundingBox, contentBox } = params;

        return [
            quantize(boundingBox.position.x),
            quantize(boundingBox.position.y),
            quantize(boundingBox.dimension.width),
            quantize(boundingBox.dimension.height),
            quantize(contentBox.position.x),
            quantize(contentBox.position.y),
            quantize(contentBox.dimension.width),
            quantize(contentBox.dimension.height),
            params.hidden,
            params.opacity,
            params.rotation,
            params.scale,
            params.blur,
            this.getValueKey(params.clipPath),
            this.getJsonId(params.clipSoftness),
            this.getJsonId(params.dropShadow),
            this.getJsonId(params.mask),
            params.usedAsInternalMask,
            params.requireTempLayer,
            params.isInAnimation,
            this.getJsonId(params.blendMode),
            quantize(params.translatedX),
            quantize(params.translatedY),
            params.isAssetLoading,
        ].join('|');
    }

    private getRegionKey(region: Region | null): string {
        return region
            ? `${quantize(region.left)},${quantize(region.top)},${quantize(region.right)},${quantize(region.bottom)}`
            : '-';
    }

    private getContent(shapedText: ShapedText, runs: Runs, context: TextCompElementContext) {
        const { sharing, position, dimension } = context;
        const stamp = [
            sharing.layoutVersion,
            this.getObjectId(shapedText),
            this.getObjectId(runs),
            dimension.getWidth(),
            dimension.getHeight(),
            context.opacity,
            this.getObjectId(context.dropShadow),
            this.getObjectId(context.blendMode),
            context.isAssetLoading,
            this.getObjectId(context.textBackground),
            context.showOversetBox,
            context.limitTextToBounds,
            position.getX() % 1,
            position.getY() % 1,
        ].join('|');
        const cached = this.elementContents.get(sharing.owner);

        if (cached?.stamp === stamp) {
            return cached;
        }

        const cacheable = !shapedText.getShapedLines().some((line) => line.glyphs.some(isDecorated));
        const checksum = this.getContentChecksum(shapedText, runs, context);
        let contentId = this.contentIds.get(checksum);

        if (contentId === undefined) {
            contentId = this.nextId++;
            this.contentIds.set(checksum, contentId);
        }

        const content = { stamp, contentId, cacheable };

        this.elementContents.set(sharing.owner, content);

        return content;
    }

    private getContentChecksum(shapedText: ShapedText, runs: Runs, context: TextCompElementContext): string {
        const { dimension, position, textBackground } = context;
        const hasGradients = runs.some(
            (run) =>
                run.color.type !== 'SOLID' || (!!run.stroke?.strokeColor && run.stroke.strokeColor.type !== 'SOLID'),
        );

        return JSON.stringify([
            dimension.getWidth(),
            dimension.getHeight(),
            context.opacity,
            context.isAssetLoading,
            context.showOversetBox,
            context.limitTextToBounds,
            context.dropShadow,
            context.blendMode,
            textBackground,
            runs,
            hasGradients ? [position.getX() % 1, position.getY() % 1] : 0,
            shapedText.getShapedLines().map((line) => [
                line.absBbox.left,
                line.absBbox.top,
                line.absBbox.width,
                line.absBbox.height,
                line.glyphs.map((glyph) => {
                    const { data } = glyph;

                    return [
                        data.text,
                        data.fontId,
                        data.glyphId,
                        this.getValueId(data.path ?? ''),
                        data.pathScale,
                        data.strokeWidth,
                        data.strokeType,
                        this.getJsonId(data.strokeColor),
                        data.punchOut ? 1 : 0,
                        this.getJsonId(shapedText.getTextPropsProperty('color', data.runIdx)),
                        this.getJsonId(shapedText.getTextPropsProperty('stroke', data.runIdx)),
                        glyph.isNewLine() ? 1 : 0,
                        data.absBbox.left,
                        data.absBbox.top,
                        data.absBbox.width,
                        data.absBbox.height,
                        data.absContentBbox.left,
                        data.absContentBbox.top,
                        data.absContentBbox.width,
                        data.absContentBbox.height,
                        data.absLeftPx,
                        data.absTopPx,
                    ];
                }),
            ]),
        ]);
    }

    private getObjectId(value: object | null | undefined): number {
        if (!value) {
            return 0;
        }

        let id = this.objectIds.get(value);

        if (id === undefined) {
            id = this.nextId++;
            this.objectIds.set(value, id);
        }

        return id;
    }

    private getValueId(value: string): number {
        let id = this.valueIds.get(value);

        if (id === undefined) {
            id = this.nextId++;
            this.valueIds.set(value, id);
        }

        return id;
    }

    private getJsonId(value: object | null | undefined): number {
        if (!value) {
            return 0;
        }

        let id = this.jsonIds.get(value);

        if (id === undefined) {
            id = this.getValueId(JSON.stringify(value));
            this.jsonIds.set(value, id);
        }

        return id;
    }

    private getValueKey(value: unknown): string {
        if (value === undefined || value === null) {
            return '-';
        }

        return typeof value === 'object' ? `o${this.getObjectId(value)}` : `v${this.getValueId(String(value))}`;
    }
}
